import React from 'react';
import { ErrorBoundary } from '@sentry/react';
import { isBacktrackEnabled } from 'src/modules/backtrack/env';

type CriticalFlow =
	| 'app_shell'
	| 'auth'
	| 'checkout'
	| 'event_page'
	| 'event_management'
	| 'event_creation'
	| 'payout_account';

type SentryBoundaryProps = {
	children: React.ReactNode;
	flow: CriticalFlow;
};

const ERROR_RETRY_STORAGE_KEY = 'last_error_retry_path';
const ERROR_RETRY_TTL_MS = 60_000;
const ERROR_RECOVERY_TARGET_PATH = '/';

type ErrorRetryState = {
	path: string;
	timestamp: number;
};

const parseErrorRetryState = (value: string | null): ErrorRetryState | null => {
	if (!value) return null;

	try {
		const parsed = JSON.parse(value) as Partial<ErrorRetryState> | null;

		if (
			parsed &&
			typeof parsed.path === 'string' &&
			typeof parsed.timestamp === 'number'
		) {
			return {
				path: parsed.path,
				timestamp: parsed.timestamp,
			};
		}
	} catch {
		return {
			path: value,
			timestamp: 0,
		};
	}

	return null;
};

const getCurrentRecoveryPath = () =>
	window.location.pathname + window.location.search;

const redirectToRecoveryTarget = () => {
	if (getCurrentRecoveryPath() === ERROR_RECOVERY_TARGET_PATH) {
		return;
	}

	window.location.replace(ERROR_RECOVERY_TARGET_PATH);
};

const ErrorRecoveryFallback = () => {
	React.useEffect(() => {
		try {
			const currentPath = getCurrentRecoveryPath();
			const lastRetry = parseErrorRetryState(
				sessionStorage.getItem(ERROR_RETRY_STORAGE_KEY)
			);
			const now = Date.now();
			const isRecentRetryForSamePath =
				lastRetry?.path === currentPath &&
				now - lastRetry.timestamp <= ERROR_RETRY_TTL_MS;

			if (isRecentRetryForSamePath) {
				sessionStorage.removeItem(ERROR_RETRY_STORAGE_KEY);
				redirectToRecoveryTarget();
			} else {
				sessionStorage.setItem(
					ERROR_RETRY_STORAGE_KEY,
					JSON.stringify({
						path: currentPath,
						timestamp: now,
					})
				);
				window.location.reload();
			}
		} catch {
			redirectToRecoveryTarget();
		}
	}, []);

	return null;
};

export const SentryBoundary = ({ children, flow }: SentryBoundaryProps) => {
	return (
		<ErrorBoundary
			beforeCapture={scope => {
				scope.setTag('area', flow);
				scope.setTag('error_source', 'render');
			}}
			onError={(error, componentStack) => {
				if (!isBacktrackEnabled()) return;
				return import('src/modules/backtrack')
					.then(({ captureBacktrackException }) => {
						captureBacktrackException(error, { source: 'react', componentStack });
					})
					.catch(() => {});
			}}
			fallback={<ErrorRecoveryFallback />}>
			{children}
		</ErrorBoundary>
	);
};
